import React from 'react';
import ReactDOM from 'react-dom';
import { X } from 'lucide-react';
import './KickModal.css';

const KickModal = ({ 
  participant, 
  tournament, 
  onConfirm, 
  onCancel, 
  loading, 
  cleanAdminName, 
  t 
}) => {
  if (!participant || !tournament) return null;

  const isRealPlayer = participant.type === 'real';
  const participantName = cleanAdminName(participant.name);
  
  // Calculate refund amount
  const refundAmount = isRealPlayer ? 
    (tournament.tournamentType === 'regular' ? tournament.entryFee : 0) :
    (participant.paidAmount || 0);

  // Get additional info for fillers
  const fillerInfo = !isRealPlayer ? {
    isMyFiller: participant.isMyFiller,
    addedBy: participant.addedBy
  } : null;

  // Use Portal to render modal at body level (escapes stacking contexts)
  return ReactDOM.createPortal(
    <div className="kick-modal-overlay">
      <div className="kick-modal">
        <div className="kick-modal-header">
          <div className="kick-modal-icon">
            {isRealPlayer ? '👤' : '🤖'}
          </div>
          <h3 className="kick-modal-title">
            {t('kickParticipant', 'Kick {type}')
              .replace('{type}', isRealPlayer ? t('player', 'Player') : t('filler', 'Filler'))}
          </h3>
          <button 
            onClick={onCancel}
            disabled={loading}
            className="kick-modal-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <div className="kick-modal-content">
          <div className="kick-modal-info">
            <div className="kick-modal-participant">
              <div className="kick-modal-participant-avatar">
                {isRealPlayer ? '👤' : '🤖'}
              </div>
              <div className="kick-modal-participant-details">
                <p className="kick-modal-participant-name">
                  {participantName}
                </p>
                <p className="kick-modal-participant-type">
                  {isRealPlayer ? t('realPlayer', 'Real Player') : t('fillerBot', 'Filler Bot')}
                </p>
              </div>
            </div>
            
            {/* Filler specific info */}
            {!isRealPlayer && fillerInfo && (
              <div className="kick-modal-filler-info">
                {fillerInfo.isMyFiller ? (
                  <div className="kick-modal-filler-ownership">
                    <span className="kick-modal-filler-my">
                      {t('yourFiller', '🤖 Your Filler')}
                    </span>
                    <span className="kick-modal-filler-note">
                      {t('youAddedThis', 'You added this filler')}
                    </span>
                  </div>
                ) : (
                  <div className="kick-modal-filler-ownership">
                    <span className="kick-modal-filler-other">
                      {t('othersFiller', '🤖 Someone else\'s filler')}
                    </span>
                    <span className="kick-modal-filler-note">
                      {t('addedBySomeoneElse', 'Added by another player')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Refund information */}
          {refundAmount > 0 && (
            <div className="kick-modal-refund">
              <div className="kick-modal-refund-header">
                <span className="kick-modal-refund-icon">💰</span>
                <span className="kick-modal-refund-title">
                  {t('refundInformation', 'Refund Information')}
                </span>
              </div>
              <div className="kick-modal-refund-details">
                <p className="kick-modal-refund-text">
                  {isRealPlayer ? (
                    t('playerRefund', 'Player will receive {amount} points refund')
                      .replace('{amount}', refundAmount)
                  ) : (
                    t('fillerRefund', 'Filler owner will receive {amount} points refund')
                      .replace('{amount}', refundAmount)
                  )}
                </p>
                {!isRealPlayer && (
                  <p className="kick-modal-refund-note">
                    {t('fillerRefundNote', 'Refund goes to the person who added the filler')}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Warning message */}
          <div className="kick-modal-warning">
            <div className="kick-modal-warning-icon">⚠️</div>
            <div className="kick-modal-warning-content">
              <p className="kick-modal-warning-title">
                {t('permanentAction', 'This action is permanent')}
              </p>
              <p className="kick-modal-warning-text">
                {isRealPlayer ? (
                  t('playerKickWarning', 'This player will be removed from the tournament and cannot rejoin.')
                ) : (
                  t('fillerKickWarning', 'This filler will be removed from the tournament and cannot be re-added.')
                )}
              </p>
            </div>
          </div>

          {/* Tournament context */}
          <div className="kick-modal-tournament-info">
            <div className="kick-modal-tournament-header">
              <span className="kick-modal-tournament-icon">🏆</span>
              <span className="kick-modal-tournament-title">
                {t('tournamentInfo', 'Tournament Information')}
              </span>
            </div>
            <div className="kick-modal-tournament-details">
              <p className="kick-modal-tournament-name">
                {cleanAdminName(tournament.creator?.inGameName || tournament.creator?.username || 'Unknown')}'s {tournament.gameMode}
              </p>
              <p className="kick-modal-tournament-stats">
                {t('currentParticipants', 'Current: {current}/{max} players')
                  .replace('{current}', tournament.participants?.length || 0)
                  .replace('{max}', tournament.maxPlayers)}
              </p>
              {tournament.fillers && tournament.fillers.length > 0 && (
                <p className="kick-modal-tournament-fillers">
                  {t('currentFillers', 'Fillers: {count}')
                    .replace('{count}', tournament.fillers.length)}
                </p>
              )}
            </div>
          </div>
        </div>
        
        <div className="kick-modal-actions">
          <button
            onClick={onConfirm}
            disabled={loading}
            className="kick-modal-btn kick-modal-btn-confirm"
          >
            {loading ? (
              <div className="kick-modal-loading">
                <div className="kick-modal-spinner"></div>
                {t('kicking', 'Kicking...')}
              </div>
            ) : (
              <>
                <span className="kick-modal-btn-icon">👢</span>
                {t('confirmKick', 'Kick {type}')
                  .replace('{type}', isRealPlayer ? t('player', 'Player') : t('filler', 'Filler'))}
              </>
            )}
          </button>
          <button
            onClick={onCancel}
            disabled={loading}
            className="kick-modal-btn kick-modal-btn-cancel"
          >
            <span className="kick-modal-btn-icon">✕</span>
            {t('cancel', 'Cancel')}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default KickModal;